import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import { AppContext } from '../../../AppProvider';

import combineStyles from '../../../utils/combineStyles';
import LayoutStyles from '../../../styles/layout';
import SpacingStyles from '../../../styles/helpers/spacing';
import TypographyStyles from '../../../styles/typography';
import ButtonStyles from '../../../styles/button';
import UploadedDocument from '../../Global/UploadedDocument';
import FormItemContainer from './../../Global/Containers/FormItemContainer';

import { filesByShipment as filesByShipmentQuery } from '../../../graphQL/queries/shipmentQueries';
import {
  triggerBolEmail as emailBOL,
  triggerLabelsEmail as emailLabels
} from '../../../graphQL/mutations/shipmentMutations';

import LoadingIndicator from '../../LoadingIndicator';
import FileUpload from '../../Global/FileUpload/FileUpload';
import MultiEmailModal from '../../common/MultiEmailModal';
import NotificationSnackbar from '../../common/NotificationSnackbar';

import { SHIPMENT_STATUS } from '../../../constants/constants';

const styles = () => ({
  root: {
    padding: 0
  },
  reloadingIcon: {
    height: [24, '!important'],
    width: [24, '!important']
  }
});

const DOC_NONE = 'DOC';
const DOC_BOL = 'BOL';
const DOC_LABELS = 'LABELS';
export class Documents extends Component {
  static propTypes = {
    values: PropTypes.object.isRequired,
    shipment: PropTypes.object.isRequired,
    editable: PropTypes.bool.isRequired
  };

  state = {
    isOpenFileUploader: false,
    files: [],
    shipmentId: null,
    reloading: false,
    snackbarOpen: false,
    snackbarMsg: ''
  };

  componentDidMount() {
    const { values } = this.props;

    this.setState({
      shipmentId: values.id,
      files: values.files || [],
      displaySendModal: false,
      sendModalTitle: '',
      docType: DOC_NONE
    });
  }

  _reloadFiles = async () => {
    const { client: ApolloClient } = this.props;
    const { currentOrgId } = this.context.globalProps;

    try {
      this.setState({ reloading: true });
      const {
        data: {
          shipment: { filesByShipment: files }
        }
      } = await ApolloClient.query({
        query: filesByShipmentQuery,
        variables: {
          orgId: currentOrgId,
          shipmentId: this.state.shipmentId
        },
        fetchPolicy: 'network-only'
      });
      this.setState({ files });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        reloading: false
      });
    }
  };

  _handleOnTagDocument = () => {};

  _handleOnDeleteDocument = () => {};

  _handleAddFile = () => this.setState({ isOpenFileUploader: true });

  _handleCloseFileUploader = () =>
    this.setState({ isOpenFileUploader: false }, this._reloadFiles);

  _handlePrintBOL = () => {
    const { shipment } = this.props;
    window.open('/BOL/' + shipment.id);
  };

  _handlePrintLabels = () => {
    const { shipment } = this.props;
    window.open('/labels/' + shipment.id);
  };

  _handleSendBOL = () => {
    const { t } = this.props;
    this.setState({
      displaySendModal: true,
      sendModalTitle: t('BOL Link Recipient(s)'),
      docType: DOC_BOL
    });
  };

  _handleSendLabels = () => {
    const { t } = this.props;
    this.setState({
      displaySendModal: true,
      sendModalTitle: t('Labels Link Recipient(s)'),
      docType: DOC_LABELS
    });
  };

  _handleSnackbarClose = () => {
    this.setState({ snackbarOpen: false, snackbarMsg: '' });
  };

  _toggleDisplaySendModal = display => {
    this.setState({ displaySendModal: display });
  };

  _sendDocLink = async addresses => {
    const { docType, shipmentId } = this.state;
    const { client, t } = this.props;
    const { currentOrgId } = this.context.globalProps;

    try {
      if (docType === DOC_BOL) {
        const response = await client.mutate({
          mutation: emailBOL,
          variables: {
            triggerBolEmails: {
              shipmentId,
              orgId: currentOrgId,
              emailAddresses: addresses
            }
          }
        });
        const {
          data: {
            shipment: { triggerBolEmail }
          }
        } = response;
        if (triggerBolEmail) {
          this.setState({
            snackbarOpen: true,
            snackbarMsg:
              addresses.length > 1 ? t('Emails sent') : t('Email sent'),
            displaySendModal: false
          });
        } else {
          this.setState({
            snackbarOpen: true,
            snackbarMsg: t('Email failed'),
            displaySendModal: false
          });
        }
      } else {
        // labels
        const response = await client.mutate({
          mutation: emailLabels,
          variables: {
            triggerLabelsEmail: {
              shipmentId,
              orgId: currentOrgId,
              emailAddresses: addresses
            }
          }
        });
        const {
          data: {
            shipment: { triggerLabelsEmail }
          }
        } = response;
        if (triggerLabelsEmail) {
          this.setState({
            snackbarOpen: true,
            snackbarMsg:
              addresses.length > 1 ? t('Emails sent') : t('Email sent'),
            displaySendModal: false
          });
        } else {
          this.setState({
            snackbarOpen: true,
            snackbarMsg: t('Email failed'),
            displaySendModal: false
          });
        }
      }
    } catch (e) {
      this.setState({
        displaySendModal: false,
        snackbarOpen: true,
        snackbarMsg: t('Email failed')
      });
    }
  };

  render() {
    const { t, classes, shipment, editable } = this.props;
    const { displaySendModal, sendModalTitle } = this.state;

    const addButton = editable && (
      <IconButton
        key="add"
        aria-label="Add"
        color="primary"
        onClick={this._handleAddFile}
        classes={{ root: classes.root }}
      >
        <AddIcon />
      </IconButton>
    );
    return (
      <Fragment>
        <FormItemContainer title={t('Documents')} secondaryAction={addButton}>
          {this.state.reloading ? (
            <LoadingIndicator classes={{ icon: classes.reloadingIcon }} />
          ) : (
            this.state.files.map(file => (
              <UploadedDocument
                key={file.id}
                file={file}
                onTag={this._handleOnTagDocument}
                onDelete={this._handleOnDeleteDocument}
                reloadFiles={this._reloadFiles}
                editable={editable}
              />
            ))
          )}
          {shipment.id && shipment.shipmentStatusId !== SHIPMENT_STATUS.SAVED && (
            <Fragment>
              <UploadedDocument
                key={'system_BOL'}
                file={{
                  tag: 'BOL',
                  uploadedFileName: 'System generated',
                  fileTypeId: 'SYSTEM'
                }}
                onTag={this._handleOnTagDocument}
                onDelete={this._handleOnDeleteDocument}
                reloadFiles={this._reloadFiles}
                printFile={this._handlePrintBOL}
                sendFile={this._handleSendBOL}
                editable={editable}
              />
              <UploadedDocument
                key={'system_label'}
                file={{
                  tag: 'LABELS',
                  uploadedFileName: t('System generated'),
                  fileTypeId: 'SYSTEM'
                }}
                onTag={this._handleOnTagDocument}
                onDelete={this._handleOnDeleteDocument}
                reloadFiles={this._reloadFiles}
                printFile={this._handlePrintLabels}
                sendFile={this._handleSendLabels}
                editable={editable}
              />
            </Fragment>
          )}
        </FormItemContainer>
        {this.state.isOpenFileUploader && (
          <FileUpload
            open={true}
            onClose={this._handleCloseFileUploader}
            params={{ shipmentId: this.state.shipmentId }}
          />
        )}
        <MultiEmailModal
          open={displaySendModal}
          onCancel={() => {
            this._toggleDisplaySendModal(false);
          }}
          onSave={this._sendDocLink}
          title={sendModalTitle}
        />
        <NotificationSnackbar
          open={this.state.snackbarOpen}
          handleSnackbarClose={this._handleSnackbarClose}
          message={this.state.snackbarMsg}
        />
      </Fragment>
    );
  }
}

const DocumentsWithTranslations = withNamespaces()(Documents);
export default withStyles(
  combineStyles(
    styles,
    TypographyStyles,
    LayoutStyles,
    SpacingStyles,
    ButtonStyles
  )
)(withApollo(DocumentsWithTranslations));
Documents.contextType = AppContext;
